



import React, { useState, useEffect, useCallback } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import { Search, ChevronLeft, ChevronRight, FileText, Download, RotateCcw, Eye, CreditCard, User } from "lucide-react";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; 
import * as XLSX from "xlsx";

const ReceiptReport = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const [tempFilters, setTempFilters] = useState({
    searchTerm: "", fromDate: "", toDate: "", paymentMode: "All", status: "All"
  });

  const [appliedFilters, setAppliedFilters] = useState({
    searchTerm: "", fromDate: "", toDate: "", paymentMode: "All", status: "All"
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "N/A" : `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  const fetchReceipts = useCallback(async (isExport = false) => {
    if (!isExport) setLoading(true);
    try {
      const pSize = isExport ? 5000 : pageSize; 
      const pNum = isExport ? 1 : currentPage;

      const queryParams = new URLSearchParams({
        pageNumber: pNum,
        pageSize: pSize,
        searchTerm: appliedFilters.searchTerm || "",
        fromDate: appliedFilters.fromDate || "",
        toDate: appliedFilters.toDate || "",
        paymentMode: appliedFilters.paymentMode, // Now sent separately
        status: appliedFilters.status            // Now sent separately
      });

      const url = `${APIConfig.APIACTIVEURL}${APIConfig.RECEIPT_GET_ALL}?${queryParams.toString()}`;
      const response = await fetch(url);
      const result = await response.json();
      
      if (isExport) return result.data || [];
      setReceipts(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) {
      console.error(e);
      return [];
    } finally {
      if (!isExport) setLoading(false);
    }
  }, [currentPage, appliedFilters]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const handleSearchClick = () => {
    setCurrentPage(1);
    setAppliedFilters({ ...tempFilters });
  };

  const handleClear = () => {
    const reset = { searchTerm: "", fromDate: "", toDate: "", paymentMode: "All", status: "All" };
    setTempFilters(reset);
    setAppliedFilters(reset);
    setCurrentPage(1);
  };

  const handleExportExcel = async () => {
    Swal.fire({ title: 'Generating Excel...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    const allData = await fetchReceipts(true);
    if (!allData.length) return Swal.fire("No Data", "No records found", "warning");
    const ws = XLSX.utils.json_to_sheet(allData.map(r => ({
      "Receipt No": r.receiptNo, "Member": r.member?.memberName, "Amount": r.amount, "Date": formatDate(r.paymentDate), "Mode": r.paymentMode, "Status": r.status
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `Receipt_Report_${Date.now()}.xlsx`);
    Swal.close();
  };

  const handleExportPDF = async () => {
    Swal.fire({ title: 'Generating PDF...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    const data = await fetchReceipts(true);
    if (!data.length) return Swal.fire("No Data", "No records found", "warning");
    const doc = new jsPDF('landscape');
    const rows = data.map(r => [r.receiptNo, r.member?.memberName, formatDate(r.paymentDate), r.paymentMode, `Rs. ${r.amount.toLocaleString()}`, r.status]);
    autoTable(doc, { head: [['Receipt #', 'Member', 'Date', 'Mode', 'Amount', 'Status']], body: rows, startY: 20, theme: 'grid', headStyles: { fillColor: [79, 70, 229] } });
    doc.save(`Receipt_Report_${Date.now()}.pdf`);
    Swal.close();
  };

  const styles = {
    container: { padding: "40px", backgroundColor: "#f4f7fe", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
    filterRow: { 
        display: "grid", 
        gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr auto auto", 
        gap: "15px", 
        marginBottom: "30px", 
        alignItems: "end", 
        backgroundColor:'#fff', 
        padding:'25px', 
        borderRadius:'20px', 
        boxShadow:'0 10px 30px rgba(0,0,0,0.05)' 
    },
    label: { fontSize: "12px", fontWeight: "800", color: "#1b2559", textTransform: "uppercase", marginBottom: '8px', display:'block' },
    input: { padding: "12px 15px", borderRadius: "12px", border: "1.5px solid #e2e8f0", fontSize: "14px", outline: "none", width: '100%', boxSizing: 'border-box' },
    searchBtn: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "14px 24px", borderRadius: "12px", cursor: "pointer", fontWeight: "700", display: 'flex', alignItems: 'center', justifyContent: 'center' },
    card: { backgroundColor: "#fff", borderRadius: "24px", boxShadow: "0 10px 40px rgba(0,0,0,0.04)", overflow: "hidden" },
    badge: (status) => ({ padding: "6px 12px", borderRadius: "8px", fontSize: "11px", fontWeight: "800", backgroundColor: status === 'SUCCESS' ? "#dcfce7" : "#fef2f2", color: status === 'SUCCESS' ? "#059669" : "#ef4444" })
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "40px" }}>
          <div>
            <h2 style={{ fontSize: "36px", fontWeight: "900", color: "#1b2559", margin: 0 }}>Receipt Reports</h2>
       
          </div>
          <div style={{ display: 'flex', gap: '15px' }}>
            <button onClick={handleExportExcel} style={{ ...styles.searchBtn, backgroundColor: '#059669' }}><Download size={20} style={{marginRight:'10px'}}/> Excel</button>
            <button onClick={handleExportPDF} style={{ ...styles.searchBtn, backgroundColor: '#e11d48' }}><FileText size={20} style={{marginRight:'10px'}}/> PDF</button>
          </div>
        </div>

        <div style={styles.filterRow}>
          <div><label style={styles.label}>Search Member / Rec #</label>
            <input style={styles.input} placeholder="Search..." value={tempFilters.searchTerm} onChange={(e) => setTempFilters({...tempFilters, searchTerm: e.target.value})} />
          </div>
          <div><label style={styles.label}>From Date</label>
            <input type="date" style={styles.input} value={tempFilters.fromDate} onChange={(e) => setTempFilters({...tempFilters, fromDate: e.target.value})} />
          </div>
          <div><label style={styles.label}>To Date</label>
            <input type="date" style={styles.input} value={tempFilters.toDate} onChange={(e) => setTempFilters({...tempFilters, toDate: e.target.value})} />
          </div>
          <div><label style={styles.label}>Mode</label>
            <select style={styles.input} value={tempFilters.paymentMode} onChange={(e) => setTempFilters({...tempFilters, paymentMode: e.target.value})}>
                <option value="All">All Modes</option><option value="Cash">Cash</option><option value="UPI">UPI</option><option value="Bank Transfer">Bank Transfer</option><option value="Cheque">Cheque</option>
            </select>
          </div>
          <div><label style={styles.label}>Status</label>
            <select style={styles.input} value={tempFilters.status} onChange={(e) => setTempFilters({...tempFilters, status: e.target.value})}>
                <option value="All">All Status</option><option value="SUCCESS">SUCCESS</option><option value="PENDING">PENDING</option><option value="FAILED">FAILED</option>
            </select>
          </div>
          <button style={styles.searchBtn} onClick={handleSearchClick}><Search size={22}/></button>
          <button style={{...styles.searchBtn, backgroundColor:'#f4f7fe', color:'#a3aed0'}} onClick={handleClear}><RotateCcw size={22}/></button>
        </div>

        <div style={styles.card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc", color: "#a3aed0", fontSize: "12px", fontWeight: "800", textTransform: "uppercase", borderBottom:'1px solid #f1f5f9' }}>
                <th style={{ padding: "25px 20px", textAlign: "left" }}>Receipt</th>
                <th style={{ padding: "25px 20px", textAlign: "left" }}>Member</th>
                <th style={{ padding: "25px 20px", textAlign: "left" }}>Details</th>
                <th style={{ padding: "25px 20px", textAlign: "left" }}>Status</th>
                <th style={{ padding: "25px 20px", textAlign: "center" }}>View</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (<tr><td colSpan="5" style={{ padding: "80px", textAlign: "center" }}>Loading...</td></tr>) : 
              receipts.map((r) => (
                <tr key={r.receiptId} style={{ borderBottom: "1px solid #f4f7fe" }}>
                  <td style={{ padding: "25px 20px" }}>
                    <div style={{ fontWeight: "900", color: "#1b2559" }}>{r.receiptNo}</div>
                    <div style={{ fontSize: "12px", color: "#a3aed0" }}>{formatDate(r.paymentDate)}</div>
                  </td>
                  <td style={{ padding: "25px 20px" }}>
                    <div style={{ fontWeight: "800", color: "#1b2559" }}><User size={12} /> {r.member?.memberName}</div>
                    <div style={{ fontSize: "12px", color: "#a3aed0" }}>{r.member?.phoneNo}</div>
                  </td>
                  <td style={{ padding: "25px 20px" }}>
                    <div style={{ fontSize: "16px", color: "#059669", fontWeight:'900' }}>₹{r.amount.toLocaleString()}</div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}><CreditCard size={12}/> {r.paymentMode}</div>
                  </td>
                  <td style={{ padding: "25px 20px" }}>
                    <div style={styles.badge(r.status)}>{r.status}</div>
                  </td>
                  <td style={{ padding: "25px 20px", textAlign: 'center' }}>
                    <button onClick={() => window.open(`/receipts`, "_blank")} style={{border:'none', background:'#eff6ff', color:'#3b82f6', padding:'12px', borderRadius:'14px', cursor:'pointer'}}><Eye size={22} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "30px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1.5px solid #f1f5f9" }}>
            <span style={{ fontSize: "15px", color: "#1b2559", fontWeight: '800' }}>Page {currentPage} of {totalPages}</span>
            <div style={{ display: "flex", gap: "15px" }}>
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={{...styles.searchBtn, padding:'10px 20px', backgroundColor: currentPage === 1 ? '#f1f5f9' : '#fff', color: '#1b2559', border: '1.5px solid #e2e8f0'}}><ChevronLeft size={20}/> Prev</button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c + 1)} style={{...styles.searchBtn, padding:'10px 20px', backgroundColor: currentPage === totalPages ? '#f1f5f9' : '#fff', color: '#1b2559', border: '1.5px solid #e2e8f0'}}>Next <ChevronRight size={20}/></button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReceiptReport;
